/**
 * Shared AWAIT_LAUNCH helper. Owns the chat-to-launch loop: idle nudges,
 * pilot-submit handling (Gate 3 ready-signal or Haiku deploy_drone),
 * and the atomic gate-3 launch tick.
 * Spec: BLUEPRINT.md § Scene 2 / § Scene 3 ("one path, not two").
 */
import { chat } from "./chat";
import type { ChatPanelApi } from "../ui/chatPanel";
import type { GameScene } from "../../../scene/src/scenes/GameScene";
import type { CubicleApi } from "../../../cubicle/src/cubicle";
import type { HistoryTurn } from "./chat";
import { isReadySignal } from "../scene1/regex";

export interface AwaitLaunchLines {
  idleAwaitLaunch30s: string;   // empty string = skip scheduling
  idleAwaitLaunch90s: string;   // empty string = skip scheduling
  launchAckRegex: string;       // fallback ack text when Haiku fires deploy_drone with empty text
}

export interface AwaitLaunchDeps {
  chat: ChatPanelApi;
  scene: GameScene;
  cubicle: CubicleApi;
  history: HistoryTurn[];       // mutated in place
  lines: AwaitLaunchLines;
  onLaunch: () => void;         // fired inside the gate-3 atomic tick, before resolve
}

const MAX_HISTORY_TURNS = 12;
const IDLE_30S_MS = 30_000;
const IDLE_90S_MS = 90_000;

export function runAwaitLaunch(deps: AwaitLaunchDeps): Promise<void> {
  return new Promise<void>((resolve) => {
    const { chat: chatPanel, scene, cubicle, history, lines, onLaunch } = deps;

    let launched = false;
    let inFlight = false;
    let idle30: ReturnType<typeof setTimeout> | null = null;
    let idle90: ReturnType<typeof setTimeout> | null = null;

    function clearIdleTimers(): void {
      if (idle30) { clearTimeout(idle30); idle30 = null; }
      if (idle90) { clearTimeout(idle90); idle90 = null; }
    }

    function scheduleIdleTimers(): void {
      clearIdleTimers();
      if (lines.idleAwaitLaunch30s) {
        idle30 = setTimeout(() => {
          if (!launched && !inFlight) void chatPanel.appendQT(lines.idleAwaitLaunch30s);
        }, IDLE_30S_MS);
      }
      if (lines.idleAwaitLaunch90s) {
        idle90 = setTimeout(() => {
          if (!launched && !inFlight) void chatPanel.appendQT(lines.idleAwaitLaunch90s);
        }, IDLE_90S_MS);
      }
    }

    scheduleIdleTimers();

    function fireLaunch(ackText: string): void {
      launched = true;
      clearIdleTimers();
      chatPanel.onPilotSubmit(() => {});
      chatPanel.blurInput();
      void chatPanel.appendQT(ackText);
      scene.startScene1Flight();
      cubicle.setLiveMonitor("game-live");
      cubicle.endPreLaunch();
      onLaunch();
      resolve();
    }

    chatPanel.onPilotSubmit((text) => {
      void (async () => {
        if (launched) return;
        chatPanel.appendPilot(text);
        if (isReadySignal(text)) { fireLaunch(lines.launchAckRegex); return; }
        inFlight = true;
        clearIdleTimers();
        try {
          const response = await chat({
            character: "qt",
            trigger: "pilot_submit",
            pilot_utterance: text,
            game_state: {},
            history: [...history],
          });
          history.push({ role: "user", content: text });
          if (response.text) history.push({ role: "assistant", content: response.text });
          while (history.length > MAX_HISTORY_TURNS) history.shift();
          if (launched) return;
          if (response.actions.some((a) => a.name === "deploy_drone")) {
            fireLaunch(response.text || lines.launchAckRegex);
          } else if (response.text) {
            await chatPanel.appendQT(response.text);
          }
        } finally {
          inFlight = false;
          if (!launched) scheduleIdleTimers();
        }
      })();
    });
  });
}
